import { LEGAL } from './legal';

export type TermsBlock =
  | { type: 'p'; text: string }
  | { type: 'ul'; items: string[] }
  | { type: 'ol'; items: string[] };

export type TermsSection = {
  id: string;
  title: string;
  blocks: TermsBlock[];
};

/**
 * Player-facing Terms of Service. Keep this aligned with what the product
 * actually ships: Cognito accounts, local / casual / ranked play, gems &
 * cosmetics via Stripe, battle pass, feedback, and AI-assisted skin packs.
 */
export const TERMS_INTRO = [
  `These Terms of Service ("Terms") are a binding agreement between you and ${LEGAL.operatorName}, an individual based in ${LEGAL.jurisdiction}, doing business as ${LEGAL.operatorDba} ("I", "we", "us", or "our"), governing your access to and use of ${LEGAL.productName} at ${LEGAL.siteUrl} (the "Service").`,
  `${LEGAL.productName} is a personal side project. It is not operated by a limited liability company, corporation, or other registered entity. By creating an account, accessing the Service, or making a purchase, you agree to these Terms. If you do not agree, do not use the Service.`,
] as const;

export const TERMS_SECTIONS: TermsSection[] = [
  {
    id: 'operator',
    title: 'Who operates the Service',
    blocks: [
      {
        type: 'p',
        text: `${LEGAL.productName} is operated by ${LEGAL.operatorName}, an individual in ${LEGAL.jurisdiction}, doing business as ${LEGAL.operatorDba}. There is no company, LLC, or corporation behind the Service. Legal notices may be sent to ${LEGAL.supportEmail}. We do not publish a street address.`,
      },
    ],
  },
  {
    id: 'eligibility',
    title: 'Eligibility',
    blocks: [
      {
        type: 'ul',
        items: [
          'You must be at least 13 years old to create an account or use the Service.',
          'If you are under 18 (or the age of majority where you live), you may use the Service only with a parent or legal guardian’s consent, and they are responsible for your use.',
          'You must be at least 18 (or the age of majority where you live) to make real-money purchases through the Service.',
          'You may not use the Service if you are barred from doing so under applicable law, or if we have previously suspended or terminated your account.',
        ],
      },
    ],
  },
  {
    id: 'accounts',
    title: 'Accounts',
    blocks: [
      {
        type: 'p',
        text: 'Some features require an account. Accounts are created with an email address and password through our authentication provider.',
      },
      {
        type: 'ul',
        items: [
          'Provide accurate information and keep your login credentials confidential. You are responsible for activity under your account.',
          'You may set a display name subject to our naming rules (length, allowed characters, uniqueness, and rename limits). We may reclaim, change, or require you to change a display name that is misleading, abusive, infringing, or otherwise inappropriate.',
          'Notify us promptly at the contact below if you believe your account has been compromised.',
          'We may refuse registration, reclaim usernames, or require verification when needed to protect the Service or other players.',
          'You may request account closure from your profile preferences. We will delete or anonymize personal data we no longer need, keeping purchase and fraud records where the law requires.',
        ],
      },
    ],
  },
  {
    id: 'service',
    title: 'The Service',
    blocks: [
      {
        type: 'p',
        text: `${LEGAL.productName} is an online and local strategy game. Features may include, without limitation:`,
      },
      {
        type: 'ul',
        items: [
          'Local (hot-seat) play, casual online matchmaking, and ranked play with Elo ratings and leaderboards.',
          'In-match features such as emotes, timers, and forfeit / quit handling.',
          'Player profiles, match history, and public ranking information.',
          'A store for virtual currency and cosmetics, and a battle pass or seasonal progression track.',
          'Community links and an authenticated feedback channel.',
        ],
      },
      {
        type: 'p',
        text: 'We may modify, suspend, or discontinue any part of the Service at any time, including game modes, rewards, matchmaking rules, seasonal content, rankings, and catalog items. We try to improve the game; we do not guarantee that any particular feature will remain available.',
      },
      {
        type: 'p',
        text: 'Some modes or features may be labeled as coming soon, experimental, or unavailable in your region. Availability can depend on maintenance, capacity, or your account status.',
      },
    ],
  },
  {
    id: 'fair-play',
    title: 'Fair play and conduct',
    blocks: [
      {
        type: 'p',
        text: 'You agree to play and interact in good faith. Without limiting other remedies, you may not:',
      },
      {
        type: 'ul',
        items: [
          'Cheat, hack, reverse engineer, or use bots, scripts, macros, or unauthorized third-party tools that automate play, manipulate matchmaking, or alter game clients or data.',
          'Exploit bugs for unfair advantage; report serious exploits to us instead.',
          'Intentionally disconnect, stall, or quit in bad faith to manipulate ratings, rewards, or opponents’ experience (ordinary disconnects and early aborts are handled by game rules, but abuse can still violate these Terms).',
          'Harass, threaten, spam, or abuse other players or our staff through display names, emotes, feedback, or other channels.',
          `Impersonate ${LEGAL.operatorName}, ${LEGAL.productName}, or another person or entity.`,
          'Share, sell, trade, or transfer accounts, virtual currency, or digital items outside channels we expressly authorize.',
          'Use the Service for any unlawful purpose, or upload or transmit malware or harmful code.',
          'Interfere with servers, networks, matchmaking, or other players’ access to the Service.',
        ],
      },
      {
        type: 'p',
        text: 'In-game “Fair Play” rules (for example, following limits and stall detection) are part of gameplay. Breaking those rules can end a match under the game engine; repeated or intentional abuse can also lead to account action under these Terms.',
      },
    ],
  },
  {
    id: 'virtual-items',
    title: 'Virtual currency and digital items',
    blocks: [
      {
        type: 'p',
        text: 'The Service may offer virtual currency and digital items, including:',
      },
      {
        type: 'ul',
        items: [
          'Gems — primarily obtained through real-money purchases and certain progression rewards.',
          'Coins — primarily earned through eligible matches and used for in-game purchases.',
          'Cosmetics and unlocks — such as army skins (including animated skin packs), profile icons, frames, emotes, and battle-pass rewards.',
        ],
      },
      {
        type: 'p',
        text: 'Important terms that apply to all virtual currency and digital items (“Virtual Items”):',
      },
      {
        type: 'ul',
        items: [
          'Virtual Items are licensed, not sold. Except for rights expressly granted, we and our licensors retain all rights.',
          'Virtual Items have no real-world cash value, are not redeemable for money, and are not bank deposits or stored-value accounts.',
          'Virtual Items are non-transferable except as we expressly allow. You may not sell, gift, or trade them outside the Service.',
          'Balances, unlocks, and progression may be adjusted for errors, fraud, exploits, chargebacks, or Service changes.',
          'We may change prices, contents, drop rates, or availability of Virtual Items at any time.',
          'If your account is suspended or terminated for breach of these Terms, you may lose access to Virtual Items without refund.',
        ],
      },
    ],
  },
  {
    id: 'purchases',
    title: 'Purchases and payments',
    blocks: [
      {
        type: 'p',
        text: 'Real-money purchases (such as gem packs or a battle pass bought with money) are processed by Stripe or another payment processor we designate. Your payment method is handled by that processor under its terms and privacy policy. We do not store your full payment card number on our servers.',
      },
      {
        type: 'ul',
        items: [
          'Prices are shown before you confirm a purchase and may change. Taxes may apply where required.',
          'By completing a purchase you authorize the charge and agree that Virtual Items will be delivered to your account when payment succeeds.',
          'Except where required by law, purchases of Virtual Items are final and non-refundable once delivered. If a technical failure prevents delivery, contact us and we will investigate in good faith.',
          'Chargebacks or payment disputes opened in bad faith, or after you have consumed the purchase, may result in account suspension and recovery of the Virtual Items.',
          'Battle pass and seasonal content may be time-limited. Buying access does not guarantee that every reward remains obtainable after a season ends if you have not claimed it under the then-current rules.',
        ],
      },
      {
        type: 'p',
        text: 'If local consumer law gives you mandatory rights that cannot be waived (for example, certain cooling-off rights), those rights apply to the extent required.',
      },
    ],
  },
  {
    id: 'feedback',
    title: 'Feedback and communications',
    blocks: [
      {
        type: 'p',
        text: 'If you submit feedback, bug reports, ideas, or other suggestions through the Service, you grant us a worldwide, perpetual, irrevocable, royalty-free license to use, copy, modify, and commercialize that material for any purpose, without obligation to you. Do not submit anything you do not have the right to share.',
      },
      {
        type: 'p',
        text: 'Outbound community links (for example Discord, Reddit, or Facebook) are third-party services. Their rules apply there; we are not responsible for those platforms.',
      },
    ],
  },
  {
    id: 'ip',
    title: 'Intellectual property',
    blocks: [
      {
        type: 'p',
        text: `The Service — including software, game design, text, graphics, audio, trademarks, and branding — is owned by ${LEGAL.operatorName} or its licensors and is protected by intellectual property laws. You receive only a limited, personal, non-exclusive, non-transferable, revocable license to use the Service as permitted by these Terms.`,
      },
      {
        type: 'p',
        text: 'Some artwork and animations in the Service (including cosmetics and skin packs) may be created with AI-assisted tools, by human artists, or both. Those designs are original for Blind Kings and are not intended to depict any third-party character. Unlocking a cosmetic grants only the in-game license described under Virtual Items, not ownership of the underlying files or the right to redistribute them.',
      },
      {
        type: 'p',
        text: 'You may not copy, modify, distribute, sell, scrape, or create derivative works from the Service or its assets except as allowed by law or with our prior written permission.',
      },
    ],
  },
  {
    id: 'third-parties',
    title: 'Third-party services',
    blocks: [
      {
        type: 'p',
        text: 'The Service relies on third parties, including cloud hosting and authentication (Amazon Web Services), payments (Stripe), website hosting (for example Netlify), and optional community destinations such as Discord. Their terms and privacy policies govern your use of those services. We are not responsible for third-party outages, content, or practices beyond our reasonable control. The Service does not show third-party advertising.',
      },
    ],
  },
  {
    id: 'privacy',
    title: 'Privacy',
    blocks: [
      {
        type: 'p',
        text: `How we collect and use personal information is described in our Privacy Policy (${LEGAL.siteUrl}/privacy). By using the Service you acknowledge that policy.`,
      },
    ],
  },
  {
    id: 'disclaimers',
    title: 'Disclaimers',
    blocks: [
      {
        type: 'p',
        text: 'THE SERVICE IS PROVIDED “AS IS” AND “AS AVAILABLE.” TO THE MAXIMUM EXTENT PERMITTED BY LAW, WE DISCLAIM ALL WARRANTIES, EXPRESS OR IMPLIED, INCLUDING MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, AND NON-INFRINGEMENT. We do not warrant that the Service will be uninterrupted, error-free, secure, or free of harmful components, or that rankings, matchmaking, or rewards will meet your expectations.',
      },
    ],
  },
  {
    id: 'liability',
    title: 'Limitation of liability',
    blocks: [
      {
        type: 'p',
        text: `TO THE MAXIMUM EXTENT PERMITTED BY LAW, ${LEGAL.operatorName.toUpperCase()} WILL NOT BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, EXEMPLARY, OR PUNITIVE DAMAGES, OR ANY LOSS OF PROFITS, DATA, GOODWILL, OR VIRTUAL ITEMS, ARISING FROM YOUR USE OF THE SERVICE.`,
      },
      {
        type: 'p',
        text: 'TO THE MAXIMUM EXTENT PERMITTED BY LAW, OUR TOTAL LIABILITY FOR ANY CLAIM RELATING TO THE SERVICE WILL NOT EXCEED THE GREATER OF (A) THE AMOUNT YOU PAID US FOR REAL-MONEY PURCHASES IN THE TWELVE MONTHS BEFORE THE CLAIM OR (B) FIFTY U.S. DOLLARS (US $50).',
      },
      {
        type: 'p',
        text: 'Some jurisdictions do not allow certain limitations; in those places our liability is limited to the fullest extent permitted.',
      },
    ],
  },
  {
    id: 'indemnity',
    title: 'Indemnification',
    blocks: [
      {
        type: 'p',
        text: `You will defend, indemnify, and hold harmless ${LEGAL.operatorName} from claims, damages, losses, and expenses (including reasonable attorneys’ fees) arising out of your use of the Service, your Virtual Items, your feedback, or your violation of these Terms or applicable law.`,
      },
    ],
  },
  {
    id: 'termination',
    title: 'Suspension and termination',
    blocks: [
      {
        type: 'p',
        text: 'You may stop using the Service at any time. We may suspend or terminate access, remove Virtual Items, reset progression, or refuse service if you breach these Terms, if we suspect fraud or abuse, if required by law, or if we discontinue the Service.',
      },
      {
        type: 'p',
        text: 'Provisions that by their nature should survive (including Virtual Items license limits, IP, disclaimers, liability limits, indemnity, and dispute terms) survive termination.',
      },
    ],
  },
  {
    id: 'changes',
    title: 'Changes to these Terms',
    blocks: [
      {
        type: 'p',
        text: 'We may update these Terms from time to time. The “Effective date” at the top will change when we do. Material changes may also be announced in the Service or by email when appropriate. Continued use after the updated Terms take effect constitutes acceptance. If you do not agree, stop using the Service.',
      },
    ],
  },
  {
    id: 'law',
    title: 'Governing law and disputes',
    blocks: [
      {
        type: 'p',
        text: `These Terms are governed by the laws of the State of Utah, United States, without regard to conflict-of-law rules, except where mandatory local consumer law applies to you.`,
      },
      {
        type: 'p',
        text: `Before filing a claim, you agree to contact us at ${LEGAL.supportEmail} and try to resolve the dispute informally within thirty (30) days. If unresolved, state or federal courts located in Utah will have exclusive jurisdiction, except that either party may seek injunctive relief in any court of competent jurisdiction to protect intellectual property or Service integrity.`,
      },
      {
        type: 'p',
        text: 'If a court finds any provision unenforceable, the remaining provisions stay in effect. These Terms are the entire agreement between you and us regarding the Service and supersede prior agreements on the same subject. Our failure to enforce a provision is not a waiver.',
      },
    ],
  },
  {
    id: 'contact',
    title: 'Contact',
    blocks: [
      {
        type: 'p',
        text: `Questions about these Terms: ${LEGAL.supportEmail}. Operator: ${LEGAL.operatorName}, an individual in ${LEGAL.jurisdiction}, doing business as ${LEGAL.operatorDba}. Service: ${LEGAL.siteUrl}.`,
      },
    ],
  },
];
